import type {
  ActorRef,
  ExecutionClass,
  IsoTimestamp,
  JsonObject,
  JsonValue,
  SecretRef,
  TenantId,
  WorkflowExecutionId,
} from "./index.js";
import type { TenantAppUserRoleKey } from "./tenant-app-user-roles.js";

export type LightweightAutomationStatus = "draft" | "active" | "paused";
export type LightweightAutomationRunStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "canceled";
export type LightweightAutomationItemRunStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "skipped";
export type LightweightAutomationModuleKey = string;
export type LightweightAutomationModuleRiskTier = "low" | "medium" | "high";

export type LightweightAutomationInputSource =
  | {
      kind: "none";
    }
  | {
      kind: "static_list";
      items: JsonValue[];
    }
  | {
      kind: "http";
      url: string;
      method?: "GET" | "POST";
      headers?: Record<string, string>;
      body?: JsonValue;
      limit?: number;
      itemsPath?: string;
    }
  | {
      kind: "webhook";
      limit?: number;
      itemsPath?: string;
    }
  | {
      kind: "connector";
      toolName: string;
      input?: JsonObject;
      itemsPath?: string;
      limit?: number;
    };

export type LightweightAutomationSupportedInputSourceKind =
  LightweightAutomationInputSource["kind"];

export interface LightweightAutomationExecutionItem {
  item: JsonValue;
  index: number;
  total: number;
}

export interface LightweightAutomationModuleDescriptor {
  key: LightweightAutomationModuleKey;
  label: string;
  description: string;
  riskTier: LightweightAutomationModuleRiskTier;
  supportedInputSources: LightweightAutomationSupportedInputSourceKind[];
}

export const BUILTIN_LIGHTWEIGHT_AUTOMATION_MODULES = [
  {
    key: "research",
    label: "Research",
    description: "Open-web research with a bounded tool surface and prompt-defined output.",
    riskTier: "low",
    supportedInputSources: ["none", "static_list", "http", "webhook"],
  },
  {
    key: "meeting_briefing",
    label: "Meeting Briefing",
    description:
      "Pre-meeting account or contact briefing with prompt-defined talking points and risks.",
    riskTier: "low",
    supportedInputSources: ["static_list", "http", "webhook", "connector"],
  },
  {
    key: "inbox_digest",
    label: "Inbox Digest",
    description: "Digest-oriented summarization for recurring updates and operational reviews.",
    riskTier: "low",
    supportedInputSources: ["none", "static_list", "http", "webhook"],
  },
  {
    key: "feedback_collector",
    label: "Feedback Collector",
    description:
      "Recurring feedback request generation for app notifications or ongoing channel messages.",
    riskTier: "low",
    supportedInputSources: ["none", "static_list", "http", "webhook"],
  },
  {
    key: "account_watch",
    label: "Account Watch",
    description:
      "Ongoing watchlist monitoring for named accounts, companies, or strategic customers.",
    riskTier: "low",
    supportedInputSources: ["static_list", "http", "webhook", "connector"],
  },
  {
    key: "document_extraction",
    label: "Document Extraction",
    description:
      "Structured extraction and summarization from documents, attachments, or referenced content.",
    riskTier: "medium",
    supportedInputSources: ["static_list", "http", "webhook"],
  },
  {
    key: "market_monitoring",
    label: "Market Monitoring",
    description:
      "Recurring market, industry, and competitor monitoring with prompt-defined result payloads.",
    riskTier: "low",
    supportedInputSources: ["static_list", "http", "webhook"],
  },
  {
    key: "csm_health_review",
    label: "CSM Health Review",
    description:
      "Customer-success account health review with risk, opportunity, and next-step guidance.",
    riskTier: "medium",
    supportedInputSources: ["static_list", "http", "webhook", "connector"],
  },
  {
    key: "hiring_research",
    label: "Hiring Research",
    description: "Candidate, company, and role research with prompt-defined briefing output.",
    riskTier: "low",
    supportedInputSources: ["static_list", "http", "webhook"],
  },
  {
    key: "legal_review_prep",
    label: "Legal Review Prep",
    description:
      "Pre-review legal or procurement summarization with clearly bounded prompt-defined output.",
    riskTier: "high",
    supportedInputSources: ["static_list", "http", "webhook"],
  },
  {
    key: "knowledge_curator",
    label: "Knowledge Curator",
    description:
      "Knowledge-base draft curation from repeated conversations, records, and source material.",
    riskTier: "medium",
    supportedInputSources: ["static_list", "http", "webhook"],
  },
] as const satisfies readonly LightweightAutomationModuleDescriptor[];

export function listBuiltinLightweightAutomationModules(): LightweightAutomationModuleDescriptor[] {
  return BUILTIN_LIGHTWEIGHT_AUTOMATION_MODULES.map((entry) => ({ ...entry }));
}

export function getBuiltinLightweightAutomationModule(
  key: string,
): LightweightAutomationModuleDescriptor | undefined {
  const match = BUILTIN_LIGHTWEIGHT_AUTOMATION_MODULES.find((entry) => entry.key === key);
  return match ? { ...match } : undefined;
}

export interface LightweightAutomationIterationPolicy {
  kind: "single" | "for_each";
  maxItemsPerRun?: number;
  concurrency?: number;
  continueOnError?: boolean;
  aggregateResults: boolean;
}

export interface LightweightAutomationAggregateCounts {
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

export interface LightweightAutomationAggregateError {
  index: number;
  errorCode?: string;
  errorMessage?: string;
}

export interface LightweightAutomationAggregateResult {
  summary: string;
  counts: LightweightAutomationAggregateCounts;
  items: JsonValue[];
  errors: LightweightAutomationAggregateError[];
}

export interface LightweightAutomationExecutionSchedule {
  kind: "cron" | "every" | "at";
  cron?: string;
  tz?: string;
  everyMs?: number;
  at?: string;
}

export interface LightweightAutomationExecution {
  mode: "manual" | "scheduled";
  schedule?: LightweightAutomationExecutionSchedule;
  executionClass: ExecutionClass;
}

export interface LightweightAutomationApiBatchDelivery {
  url: string;
  authTokenSecretRef?: SecretRef;
  headers?: Record<string, string>;
  timeoutMs?: number;
  bodyShape: "automation.result.v1" | "automation.webhook.v1" | "json_template.v1";
  bodyTemplate?: string;
}

export interface LightweightAutomationWebhookDelivery {
  url: string;
  authTokenSecretRef?: SecretRef;
  headers?: Record<string, string>;
  timeoutMs?: number;
  bodyShape?: "automation.result.v1" | "automation.webhook.v1" | "json_template.v1";
  bodyTemplate?: string;
}

export interface LightweightAutomationAppNotificationTarget {
  userIds?: string[];
  roleKeys?: TenantAppUserRoleKey[];
}

export type LightweightAutomationAudienceKind = "self" | "users" | "roles" | "active_sessions";

export interface LightweightAutomationAudience {
  kind: LightweightAutomationAudienceKind;
  userIds?: string[];
  roleKeys?: TenantAppUserRoleKey[];
  channels?: string[];
  activeWithinDays?: number;
  limit?: number;
}

export interface LightweightAutomationSessionEventDeliveryTarget {
  targetUserId?: string;
  delivery?: {
    channel: string;
    to: string;
    accountId?: string;
    threadId?: string;
  };
  instruction?: string;
}

export type LightweightAutomationPublishTarget =
  | {
      kind: "app_notification";
      appNotification?: LightweightAutomationAppNotificationTarget;
    }
  | {
      kind: "session_event";
      sessionEvent?: LightweightAutomationSessionEventDeliveryTarget;
    };

export interface LightweightAutomationMessageDelivery {
  destination: "inbox" | "email" | "channel";
  targetRef?: string;
  publishTarget?: LightweightAutomationPublishTarget;
}

export interface LightweightAutomationDelivery {
  mode: "none" | "api_batch" | "webhook" | "message";
  apiBatch?: LightweightAutomationApiBatchDelivery;
  webhook?: LightweightAutomationWebhookDelivery;
  message?: LightweightAutomationMessageDelivery;
}

export interface LightweightAutomationResult {
  summary: string;
  title?: string;
  content?: string;
  payload: JsonValue;
}

export interface LightweightAutomationDeliveryBodyContext {
  automationId?: string;
  runId?: string;
  generatedAt: IsoTimestamp;
  moduleKey: LightweightAutomationModuleKey | string;
  deliveryMode: "api_batch" | "webhook";
  result: LightweightAutomationResult;
  compatibility?: JsonObject;
}

const AUTOMATION_DELIVERY_TEMPLATE_EXACT_PATTERN = /^\s*\{\{\s*([a-zA-Z0-9_.[\]]+)\s*\}\}\s*$/;
const AUTOMATION_DELIVERY_TEMPLATE_INLINE_PATTERN = /\{\{\s*([a-zA-Z0-9_.[\]]+)\s*\}\}/g;

export const LIGHTWEIGHT_AUTOMATION_TEMPLATE_VARIABLES = [
  "automationId",
  "runId",
  "generatedAt",
  "moduleKey",
  "deliveryMode",
  "result",
  "result.title",
  "result.content",
  "result.summary",
  "result.payload",
  "items",
  "firstItem",
] as const;

export type LightweightAutomationTemplateVariable =
  (typeof LIGHTWEIGHT_AUTOMATION_TEMPLATE_VARIABLES)[number];

export function buildDefaultLightweightAutomationApiBatchBody(
  context: LightweightAutomationDeliveryBodyContext,
): JsonObject {
  return {
    automationId: context.automationId ?? "",
    runId: context.runId ?? "",
    generatedAt: context.generatedAt,
    moduleKey: context.moduleKey,
    result: {
      ...(context.result.title ? { title: context.result.title } : {}),
      ...(context.result.content ? { content: context.result.content } : {}),
      summary: context.result.summary,
      payload: context.result.payload,
    },
  };
}

export function buildDefaultLightweightAutomationWebhookBody(
  context: LightweightAutomationDeliveryBodyContext,
): JsonObject {
  return {
    automationId: context.automationId ?? "",
    runId: context.runId ?? "",
    generatedAt: context.generatedAt,
    moduleKey: context.moduleKey,
    deliveryMode: context.deliveryMode,
    result: {
      ...(context.result.title ? { title: context.result.title } : {}),
      ...(context.result.content ? { content: context.result.content } : {}),
      summary: context.result.summary,
      payload: context.result.payload,
    },
  };
}

export function renderLightweightAutomationDeliveryBody(params: {
  bodyShape: "automation.result.v1" | "automation.webhook.v1" | "json_template.v1";
  bodyTemplate?: string;
  context: LightweightAutomationDeliveryBodyContext;
}): JsonValue {
  switch (params.bodyShape) {
    case "automation.result.v1":
      return buildDefaultLightweightAutomationApiBatchBody(params.context);
    case "automation.webhook.v1":
      return buildDefaultLightweightAutomationWebhookBody(params.context);
    case "json_template.v1":
      return renderLightweightAutomationDeliveryJsonTemplate(params.bodyTemplate, params.context);
    default:
      return buildDefaultLightweightAutomationApiBatchBody(params.context);
  }
}

export function renderLightweightAutomationDeliveryJsonTemplate(
  template: string | undefined,
  context: LightweightAutomationDeliveryBodyContext,
): JsonValue {
  const trimmed = template?.trim();
  if (!trimmed) {
    throw new Error("Custom automation delivery body templates cannot be empty.");
  }
  const parsed = JSON.parse(trimmed) as JsonValue;
  return materializeLightweightAutomationTemplateValue(parsed, context, "$");
}

function materializeLightweightAutomationTemplateValue(
  value: JsonValue,
  context: LightweightAutomationDeliveryBodyContext,
  path: string,
): JsonValue {
  if (typeof value === "string") {
    return materializeLightweightAutomationTemplateString(value, context, path);
  }
  if (Array.isArray(value)) {
    return value.map((entry, index) =>
      materializeLightweightAutomationTemplateValue(entry, context, `${path}[${index}]`),
    );
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        materializeLightweightAutomationTemplateValue(entry, context, `${path}.${key}`),
      ]),
    );
  }
  return value;
}

function materializeLightweightAutomationTemplateString(
  value: string,
  context: LightweightAutomationDeliveryBodyContext,
  path: string,
): JsonValue {
  const exactMatch = value.match(AUTOMATION_DELIVERY_TEMPLATE_EXACT_PATTERN);
  if (exactMatch) {
    return resolveLightweightAutomationTemplateVariable(exactMatch[1]!, context, path);
  }

  return value.replace(AUTOMATION_DELIVERY_TEMPLATE_INLINE_PATTERN, (_match, variablePath) => {
    const resolved = resolveLightweightAutomationTemplateVariable(variablePath, context, path);
    if (resolved === null) {
      return "null";
    }
    if (
      typeof resolved === "string" ||
      typeof resolved === "number" ||
      typeof resolved === "boolean"
    ) {
      return String(resolved);
    }
    throw new Error(
      `Template variable "{{${variablePath}}}" at ${path} resolves to a non-scalar value and must be used as a full JSON value.`,
    );
  });
}

function resolveLightweightAutomationTemplateVariable(
  variablePath: string,
  context: LightweightAutomationDeliveryBodyContext,
  path: string,
): JsonValue {
  const payload =
    context.result.payload && typeof context.result.payload === "object"
      ? (context.result.payload as JsonObject)
      : null;
  const items = Array.isArray(payload?.items) ? payload.items : null;
  const root: JsonObject = {
    automationId: context.automationId ?? null,
    runId: context.runId ?? null,
    generatedAt: context.generatedAt,
    moduleKey: context.moduleKey,
    deliveryMode: context.deliveryMode,
    result: {
      title: context.result.title ?? null,
      content: context.result.content ?? null,
      summary: context.result.summary,
      payload: context.result.payload,
    },
    items: items ?? null,
    firstItem: items?.[0] ?? null,
    ...(context.compatibility ? context.compatibility : {}),
  };

  let current: JsonValue = root;
  for (const segment of splitAutomationDeliveryTemplatePath(variablePath, path)) {
    if (!segment) {
      throw new Error(`Invalid template variable "{{${variablePath}}}" at ${path}.`);
    }
    if (Array.isArray(current)) {
      const index = Number.parseInt(segment, 10);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) {
        throw new Error(`Unknown template variable "{{${variablePath}}}" at ${path}.`);
      }
      current = current[index] as JsonValue;
      continue;
    }
    if (!current || typeof current !== "object" || !(segment in current)) {
      throw new Error(`Unknown template variable "{{${variablePath}}}" at ${path}.`);
    }
    current = (current as JsonObject)[segment] as JsonValue;
  }

  return current;
}

function splitAutomationDeliveryTemplatePath(variablePath: string, path: string): string[] {
  const normalized = variablePath.replace(/\[(\d+)\]/g, ".$1");
  if (normalized.includes("[") || normalized.includes("]")) {
    throw new Error(`Invalid template variable "{{${variablePath}}}" at ${path}.`);
  }
  return normalized.split(".");
}

export interface LightweightAutomationRecord {
  tenantId: TenantId;
  automationId: string;
  name: string;
  enabled: boolean;
  agentId: string;
  ownerMode: LightweightAutomationOwnerMode;
  ownerUserId: string;
  audience?: LightweightAutomationAudience;
  inboxRecipients?: LightweightAutomationInboxRecipients;
  moduleKey: LightweightAutomationModuleKey | string;
  instructionsMarkdown: string;
  templateRef?: string;
  inputSource: LightweightAutomationInputSource;
  iterationPolicy: LightweightAutomationIterationPolicy;
  execution: LightweightAutomationExecution;
  delivery: LightweightAutomationDelivery;
  status?: LightweightAutomationStatus;
  lastRunAt?: IsoTimestamp;
  nextRunAt?: IsoTimestamp;
  lastRunStatus?: LightweightAutomationRunStatus;
  lastRunSummary?: string;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
  deletedAt?: IsoTimestamp;
}

export type LightweightAutomationOwnerMode = "self" | "shared";

export interface LightweightAutomationInboxRecipients {
  userIds?: string[];
  roleKeys?: TenantAppUserRoleKey[];
}

export type AutomationNotificationStatus = "unread" | "read";

export interface AutomationResultRecord {
  resultId: string;
  tenantId: TenantId;
  automationId: string;
  automationName: string;
  runId: string;
  agentId: string;
  moduleKey: string;
  ownerMode: LightweightAutomationOwnerMode;
  ownerUserId: string;
  title?: string;
  content?: string;
  summary: string;
  payload?: JsonValue;
  workflowResult?: JsonValue;
  generatedAt: IsoTimestamp;
}

export interface AutomationNotificationRecord {
  notificationId: string;
  resultId: string;
  tenantId: TenantId;
  userId: string;
  automationId: string;
  automationName: string;
  runId: string;
  ownerMode: LightweightAutomationOwnerMode;
  status: AutomationNotificationStatus;
  summary: string;
  createdAt: IsoTimestamp;
  readAt?: IsoTimestamp;
}

export interface LightweightAutomationRunRecord {
  runId: string;
  tenantId: TenantId;
  automationId: string;
  agentId: string;
  trigger: "manual" | "scheduled";
  requestedBy: ActorRef;
  requestedAt: IsoTimestamp;
  startedAt?: IsoTimestamp;
  completedAt?: IsoTimestamp;
  status: LightweightAutomationRunStatus;
  executionId?: WorkflowExecutionId;
  input: JsonObject;
  summary?: string;
  result?: JsonValue;
  errorCode?: string;
  errorMessage?: string;
}

export interface LightweightAutomationItemRunRecord {
  itemRunId: string;
  runId: string;
  tenantId: TenantId;
  automationId: string;
  index: number;
  status: LightweightAutomationItemRunStatus;
  itemInput: JsonValue;
  summary?: string;
  result?: JsonValue;
  errorCode?: string;
  errorMessage?: string;
  startedAt?: IsoTimestamp;
  completedAt?: IsoTimestamp;
}

export interface CompiledLightweightAutomationWorkflowInputV2 {
  automationId: string;
  runId: string;
  generatedAt: IsoTimestamp;
  moduleKey: LightweightAutomationModuleKey | string;
  instructionsMarkdown: string;
  inputSource: LightweightAutomationInputSource;
  iterationPolicy: LightweightAutomationIterationPolicy;
  delivery: LightweightAutomationDelivery;
}

export function createDefaultLightweightAutomationInputSource(): LightweightAutomationInputSource {
  return { kind: "none" };
}

export function createDefaultLightweightAutomationIterationPolicy(): LightweightAutomationIterationPolicy {
  return {
    kind: "single",
    aggregateResults: true,
  };
}

export function buildCompiledLightweightAutomationWorkflowInput(params: {
  automation: LightweightAutomationRecord;
  runId: string;
  generatedAt: IsoTimestamp;
}): CompiledLightweightAutomationWorkflowInputV2 {
  return {
    automationId: params.automation.automationId,
    runId: params.runId,
    generatedAt: params.generatedAt,
    moduleKey: params.automation.moduleKey,
    instructionsMarkdown: params.automation.instructionsMarkdown,
    inputSource: JSON.parse(
      JSON.stringify(params.automation.inputSource),
    ) as LightweightAutomationInputSource,
    iterationPolicy: JSON.parse(
      JSON.stringify(params.automation.iterationPolicy),
    ) as LightweightAutomationIterationPolicy,
    delivery: JSON.parse(
      JSON.stringify(params.automation.delivery),
    ) as LightweightAutomationDelivery,
  };
}

export function buildLightweightAutomationWorkflowInputSummary(
  automation: Pick<LightweightAutomationRecord, "name" | "moduleKey" | "instructionsMarkdown">,
): string {
  const name = automation.name.trim();
  if (name) {
    return name;
  }
  const instructions = automation.instructionsMarkdown.trim();
  if (instructions) {
    return instructions.split(/\r?\n/, 1)[0]?.trim() || `Run ${automation.moduleKey} automation`;
  }
  return `Run ${automation.moduleKey} automation`;
}
